import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import { reportEvent } from '~/domains/analytics/events';
import { PromotionsModalsContext } from '~/domains/promotions/Modals/PromotionsModalsContext';

import { useGamificationContext } from '../../../context/GamificationContext';
import type { RewardBasis } from '../../../types/gamification.types';
import { WHALERS_CLUB_DISMISSED_KEY } from '../constants';
import type { TierName, TierStatus } from '../types/whalersClub.types';
import { getTierRewardPct } from '../utils/getTierRewardPct';

export const useWhalersClub = () => {
  const [isDismissed, setIsDismissed] = useState(false);
  const { openModalUnique } = useContext(PromotionsModalsContext);
  const {
    activeProgram,
    activeProgramStatus,
    isLoyaltyProgramsLoading,
    isActiveProgramStatusLoading,
    refetchActiveProgramStatus,
  } = useGamificationContext();

  const programId = activeProgram?.id ?? null;
  const tiers = useMemo(() => activeProgram?.tiers ?? [], [activeProgram]);
  const faq = activeProgram?.faq ?? null;
  const whatQualifies = activeProgram?.whatQualifies ?? '';
  const whatDoesNotQualify = activeProgram?.whatDoesNotQualify ?? '';

  // Tiers transformation
  const tierStatus: TierStatus | null = useMemo(() => {
    if (!(tiers?.length > 0) || !activeProgramStatus) return null;

    const { currentPeriod, currentTier, activity, progress, projectedReward } = activeProgramStatus;
    const currentTierIndex = tiers.findIndex((tier) => tier.id === currentTier?.id);
    const rewardBasis: RewardBasis = activeProgramStatus.rewardBasis === 'net_loss' ? 'net_loss' : 'handle';

    return {
      rewardBasis,
      periodStartAt: new Date(currentPeriod.periodStartAt),
      periodEndAt: new Date(currentPeriod.periodEndAt),

      currentTierName: currentTier?.name as TierName,
      currentTierIndex,
      currentRewardAmount: match(rewardBasis)
        .with('handle', () => (currentTier?.rewardRates[0]?.rateValue ?? 0) * activity.metricValue)
        .with('net_loss', () => projectedReward?.[0]?.amount ?? 0)
        .exhaustive(),
      currentTierRewardPct: getTierRewardPct(currentTier ?? undefined),
      currentProgressAmount: activity.metricValue,
      currentProgressPct: progress.progressPercent,

      nextTierName: progress.nextTier?.name as TierName,
      nextTierRemainingAmount: Math.round(progress.amountToNextTier),

      isLastTier: currentTierIndex === tiers.length - 1,
    }
  }, [tiers, activeProgramStatus]);

  // Modal handlers
  const openConnectedWhalersClubRewardHistoryModal = useCallback(() => {
    reportEvent('Promotion > Whalers Club - View History', {
      currentTier: tierStatus?.currentTierName,
      currentSpend: tierStatus?.currentProgressAmount,
      currentReward: tierStatus?.currentRewardAmount,
    });

    openModalUnique({
      type: 'ConnectedWhalersClubRewardHistoryModal',
      isClosing: true,
      programId,
    });
  }, [programId, tierStatus, openModalUnique]);

  const openWhalersClubDetailModal = useCallback(() => {
    reportEvent('Promotion > Whalers Club - View Detail', {
      currentTier: tierStatus?.currentTierName,
      currentSpend: tierStatus?.currentProgressAmount,
      currentReward: tierStatus?.currentRewardAmount,
    });

    openModalUnique ({
      type: 'WhalersClubDetailModal',
      isClosing: true,
      tiers,
      tierStatus,
      faq,
      whatQualifies,
      whatDoesNotQualify,
      handleOpenHistoryModal: openConnectedWhalersClubRewardHistoryModal,
    });
  }, [
    tiers,
    tierStatus,
    faq,
    whatQualifies,
    whatDoesNotQualify,
    openModalUnique,
    openConnectedWhalersClubRewardHistoryModal,
  ]);

  // Promo card dismiss
  useEffect(() => {
    if (tierStatus?.currentProgressAmount > 0) {
      setIsDismissed(false);
    } else {
      setIsDismissed(localStorage.getItem(WHALERS_CLUB_DISMISSED_KEY) === 'true');
    }
  }, [tierStatus]);

  const handleDismissPromoCard = useCallback(() => {
    reportEvent('Promotion > Whalers Club - Dismiss');

    setIsDismissed(true);
    localStorage.setItem(WHALERS_CLUB_DISMISSED_KEY, 'true');
  }, []);

  return {
    isLoading: isLoyaltyProgramsLoading || isActiveProgramStatusLoading,
    tiers,
    tierStatus,
    refetchActiveProgramStatus,
    openWhalersClubDetailModal,
    handleDismissPromoCard,
    isPromoCardDismissed: isDismissed,
  };
}
